// Page Cinéma Mobile - Design unifié
// Header simple "Cinéma" + Onglets Tous/Mes amis/Mes Pros + Loupe recherche fullscreen
// Grille de vidéos + Mode Reels fullscreen au clic

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Clock, Play, Heart, MessageCircle, Share2, Loader2,
  RefreshCw, SlidersHorizontal, User, Users, Briefcase, Eye,
  Volume2, VolumeX, ChevronUp, ChevronDown, Pause, Filter
} from 'lucide-react';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des métiers pour le filtre
const METIERS = [
  { value: 'tous', label: 'Tous', icon: '🏠' },
  { value: 'plombier', label: 'Plombier', icon: '💧' },
  { value: 'electricien', label: 'Électricien', icon: '⚡' },
  { value: 'macon', label: 'Maçon', icon: '🧱' },
  { value: 'carreleur', label: 'Carreleur', icon: '🔲' },
  { value: 'peintre', label: 'Peintre', icon: '🎨' },
  { value: 'menuisier', label: 'Menuisier', icon: '🔨' },
  { value: 'couvreur', label: 'Couvreur', icon: '🏠' },
  { value: 'chauffagiste', label: 'Chauffagiste', icon: '🔥' },
  { value: 'pisciniste', label: 'Pisciniste', icon: '🏊' },
  { value: 'paysagiste', label: 'Paysagiste', icon: '🌿' },
];

// Mock videos data
const MOCK_VIDEOS = [
  {
    id: 'vid-1',
    title: 'Rénovation complète salle de bain en 5 jours',
    thumbnail: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=400&h=600&fit=crop',
    video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '02:34',
    views: 12500,
    likes: 843,
    comments: 56,
    author: { name: 'Martin Plomberie', avatar: null, is_pro: true, metier: 'Plombier' },
    created_at: new Date(Date.now() - 2 * 24 * 3600000).toISOString()
  },
  {
    id: 'vid-2',
    title: 'Comment poser du carrelage comme un pro',
    thumbnail: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=600&fit=crop',
    video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '05:12',
    views: 8700,
    likes: 621,
    comments: 34,
    author: { name: 'CarrelPro', avatar: null, is_pro: true, metier: 'Carreleur' },
    created_at: new Date(Date.now() - 5 * 24 * 3600000).toISOString()
  },
  {
    id: 'vid-3',
    title: 'Mon chantier du jour - Isolation combles',
    thumbnail: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=600&fit=crop',
    video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '01:45',
    views: 3200,
    likes: 234,
    comments: 12,
    author: { name: 'Jean Dupont', avatar: null, is_pro: false, is_friend: true },
    created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString()
  },
  {
    id: 'vid-4',
    title: 'Astuce électricien : câblage tableau',
    thumbnail: 'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=400&h=600&fit=crop',
    video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '03:28',
    views: 15600,
    likes: 1102,
    comments: 89,
    author: { name: 'ElecTech', avatar: null, is_pro: true, metier: 'Électricien' },
    created_at: new Date(Date.now() - 3 * 24 * 3600000).toISOString()
  },
  {
    id: 'vid-5',
    title: 'Avant/Après : Rénovation cuisine',
    thumbnail: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=600&fit=crop',
    video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '04:56',
    views: 22100,
    likes: 1876,
    comments: 145,
    author: { name: 'Cuisines Martin', avatar: null, is_pro: true, metier: 'Cuisiniste' },
    created_at: new Date(Date.now() - 7 * 24 * 3600000).toISOString()
  },
  {
    id: 'vid-6',
    title: 'DIY : Repeindre sa chambre',
    thumbnail: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=400&h=600&fit=crop',
    video_url: 'https://www.w3schools.com/html/mov_bbb.mp4',
    duration: '02:15',
    views: 4500,
    likes: 312,
    comments: 23,
    author: { name: 'Sophie B.', avatar: null, is_pro: false, is_friend: true },
    created_at: new Date(Date.now() - 4 * 24 * 3600000).toISOString()
  },
];

// Format nombre
const formatNumber = (num) => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
  return num.toString();
};

// Carte Vidéo (grille)
function VideoCard({ video, onClick }) {
  const isPro = video.author?.is_pro;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative rounded-xl overflow-hidden bg-gray-900 cursor-pointer group"
      style={{ aspectRatio: '9/16' }}
      onClick={() => onClick(video)}
    >
      {/* Thumbnail */}
      <img 
        src={video.thumbnail} 
        alt={video.title}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      
      {/* Overlay gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
      
      {/* Play button center */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-14 h-14 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
          <Play size={28} className="text-white ml-1" fill="white" />
        </div>
      </div>
      
      {/* Duration badge */}
      <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/70 rounded text-white text-[10px] font-medium">
        {video.duration}
      </div>
      
      {/* Pro badge */}
      {isPro && (
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#1E3A5F] rounded text-white text-[10px] font-bold flex items-center gap-1">
          <Briefcase size={10} /> PRO
        </div>
      )}
      
      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        {/* Author */}
        <div className="flex items-center gap-2 mb-2">
          <div 
            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
            style={{ background: isPro ? '#1E3A5F' : '#F97316' }}
          >
            {video.author?.name?.[0] || 'A'}
          </div>
          <span className="text-white text-xs font-medium truncate">{video.author?.name}</span>
        </div>
        
        {/* Title */}
        <p className="text-white text-xs font-medium line-clamp-2 leading-tight mb-2">
          {video.title}
        </p>
        
        {/* Stats */}
        <div className="flex items-center gap-3 text-white/80 text-[10px]">
          <span className="flex items-center gap-1">
            <Eye size={12} /> {formatNumber(video.views)}
          </span>
          <span className="flex items-center gap-1">
            <Heart size={12} /> {formatNumber(video.likes)}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// Modal Reels Fullscreen
function ReelsPlayer({ videos, initialIndex, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [liked, setLiked] = useState({});
  const videoRef = useRef(null);
  
  const currentVideo = videos[currentIndex];
  
  const goNext = () => {
    if (currentIndex < videos.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsPlaying(true);
    }
  };
  
  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsPlaying(true);
    }
  };
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  
  const toggleLike = () => {
    setLiked(prev => ({ ...prev, [currentVideo.id]: !prev[currentVideo.id] }));
    if (!liked[currentVideo.id]) {
      toast.success('Vidéo likée !');
    }
  };
  
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Lien copié !');
  };
  
  // Handle swipe
  const [touchStart, setTouchStart] = useState(0);
  const handleTouchStart = (e) => setTouchStart(e.touches[0].clientY);
  const handleTouchEnd = (e) => {
    const diff = touchStart - e.changedTouches[0].clientY;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goNext();
      else goPrev();
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[99999] bg-black"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Video */}
      <div className="absolute inset-0" onClick={togglePlay}>
        <video
          ref={videoRef}
          src={currentVideo.video_url}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted={isMuted}
          playsInline
        />
        
        {/* Pause indicator */}
        <AnimatePresence>
          {!isPlaying && (
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-20 h-20 rounded-full bg-black/50 flex items-center justify-center">
                <Play size={40} className="text-white ml-2" fill="white" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}>
        <button onClick={onClose} className="p-2 rounded-full bg-black/30 backdrop-blur-sm">
          <X size={24} className="text-white" />
        </button>
        <button onClick={() => setIsMuted(!isMuted)} className="p-2 rounded-full bg-black/30 backdrop-blur-sm">
          {isMuted ? <VolumeX size={24} className="text-white" /> : <Volume2 size={24} className="text-white" />}
        </button>
      </div>
      
      {/* Navigation arrows */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 z-10">
        <button 
          onClick={goPrev} 
          disabled={currentIndex === 0}
          className={`p-2 rounded-full bg-black/30 backdrop-blur-sm ${currentIndex === 0 ? 'opacity-30' : ''}`}
        >
          <ChevronUp size={24} className="text-white" />
        </button>
        <button 
          onClick={goNext}
          disabled={currentIndex === videos.length - 1}
          className={`p-2 rounded-full bg-black/30 backdrop-blur-sm ${currentIndex === videos.length - 1 ? 'opacity-30' : ''}`}
        >
          <ChevronDown size={24} className="text-white" />
        </button>
      </div>
      
      {/* Right side actions */}
      <div className="absolute right-4 bottom-32 flex flex-col items-center gap-6 z-10">
        {/* Like */}
        <button onClick={toggleLike} className="flex flex-col items-center gap-1">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${liked[currentVideo.id] ? 'bg-red-500' : 'bg-black/30 backdrop-blur-sm'}`}>
            <Heart size={26} className="text-white" fill={liked[currentVideo.id] ? 'white' : 'none'} />
          </div>
          <span className="text-white text-xs font-medium">{formatNumber(currentVideo.likes + (liked[currentVideo.id] ? 1 : 0))}</span>
        </button>
        
        {/* Comments */}
        <button className="flex flex-col items-center gap-1">
          <div className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
            <MessageCircle size={26} className="text-white" />
          </div>
          <span className="text-white text-xs font-medium">{formatNumber(currentVideo.comments)}</span>
        </button>
        
        {/* Share */}
        <button onClick={handleShare} className="flex flex-col items-center gap-1">
          <div className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
            <Share2 size={26} className="text-white" />
          </div>
          <span className="text-white text-xs font-medium">Partager</span>
        </button>
      </div>
      
      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-20 p-4 z-10" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom))' }}>
        {/* Author */}
        <div className="flex items-center gap-3 mb-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
            style={{ background: currentVideo.author?.is_pro ? '#1E3A5F' : '#F97316' }}
          >
            {currentVideo.author?.name?.[0] || 'A'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-white font-semibold">{currentVideo.author?.name}</span>
              {currentVideo.author?.is_pro && (
                <span className="px-1.5 py-0.5 bg-[#1E3A5F] text-white text-[10px] font-bold rounded">PRO</span>
              )}
            </div>
            {currentVideo.author?.metier && (
              <span className="text-white/70 text-sm">{currentVideo.author.metier}</span>
            )}
          </div>
        </div>
        
        {/* Title */}
        <p className="text-white text-sm font-medium leading-tight">
          {currentVideo.title}
        </p>
      </div>
      
      {/* Progress indicator */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
        <div 
          className="h-full bg-white transition-all"
          style={{ width: `${((currentIndex + 1) / videos.length) * 100}%` }}
        />
      </div>
    </motion.div>
  );
}

export default function MobileCinemaPage({ isDesktopEmbed = false }) {
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(null);
  
  // Filtres
  const [activeTab, setActiveTab] = useState('tous');
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [showMetierFilter, setShowMetierFilter] = useState(false);
  const [selectedMetier, setSelectedMetier] = useState('tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    fetchVideos();
  }, [activeTab, selectedMetier]);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem('cinema_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  const fetchVideos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reseau/videos?limit=20`);
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
      } else {
        // API non disponible - état vide pour production
        setVideos([]);
      }
    } catch (e) {
      // Erreur réseau - état vide pour production
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedMetier]);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchVideos();
    setRefreshing(false);
  };
  
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try { localStorage.setItem('cinema_recent_searches', JSON.stringify(updated)); } catch {}
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('cinema_recent_searches'); } catch {}
  };
  
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };
  
  const handleVideoClick = (video) => {
    const index = videos.findIndex(v => v.id === video.id);
    setSelectedVideoIndex(index >= 0 ? index : 0);
  };
  
  return (
    <div className={isDesktopEmbed ? "bg-white" : "min-h-screen bg-gray-50"} style={{ paddingBottom: isDesktopEmbed ? '0' : '80px' }}>
      {/* Barre Bêta + Header - Masqués en mode Desktop embed */}
      {!isDesktopEmbed && (
        <>
          {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}
          
          <header className="bg-white border-b border-gray-200 sticky top-[30px] z-50">
            <div className="flex items-center justify-between h-14 px-3">
              <div className="flex items-center gap-0.5">
                <h1 className="text-lg font-bold text-[#1E3A5F]">Cinéma</h1>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleRefresh} disabled={refreshing} className="p-2 hover:bg-gray-100 rounded-full">
                  <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button 
                  onClick={() => setShowMetierFilter(!showMetierFilter)}
                  className={`p-2 rounded-full transition-colors ${showMetierFilter ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'}`}
                >
                  <Filter size={20} />
                </button>
                <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="cinema-search-btn">
                  <Search size={20} className="text-gray-600" />
                </button>
              </div>
            </div>
            
            <div className="flex border-b border-gray-100">
              <button onClick={() => setActiveTab('tous')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'tous' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}>
                <Users size={14} /> Tous
              </button>
              <button onClick={() => setActiveTab('mes_amis')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'mes_amis' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}>
                <User size={14} /> Mes amis
              </button>
              <button onClick={() => setActiveTab('mes_pros')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'mes_pros' ? 'text-[#1E3A5F] border-b-2 border-[#1E3A5F]' : 'text-gray-500'}`}>
                <Briefcase size={14} /> Mes Pros
              </button>
            </div>
            
            <AnimatePresence>
              {showMetierFilter && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden bg-gray-50"
                >
                  <div className="flex gap-2 p-3 overflow-x-auto">
                    {METIERS.map(m => (
                      <button
                        key={m.value}
                        onClick={() => setSelectedMetier(m.value)}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
                          selectedMetier === m.value
                            ? 'bg-orange-500 text-white'
                            : 'bg-white text-gray-700 border border-gray-200'
                        }`}
                      >
                        <span>{m.icon}</span>
                        <span>{m.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </header>
        </>
      )}
      
      {/* Contenu */}
      <div className="p-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Play size={32} className="text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              {activeTab === 'mes_pros' ? 'Aucune vidéo de tes pros' : activeTab === 'mes_amis' ? 'Aucune vidéo de tes amis' : 'Aucune vidéo'}
            </h3>
            <p className="text-sm text-gray-500">
              {activeTab === 'mes_pros' ? 'Suis des professionnels pour voir leurs vidéos' : activeTab === 'mes_amis' ? 'Tes amis n\'ont pas encore partagé de vidéos' : 'Sois le premier à partager !'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} onClick={handleVideoClick} />
            ))}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#ffffff', zIndex: 99999 }}>
            <div className="flex items-center justify-end" style={{ padding: '8px 12px', paddingTop: 'max(8px, env(safe-area-inset-top, 8px))' }}>
              <button onClick={() => setShowFullscreenSearch(false)} style={{ width: '32px', height: '32px' }}>
                <X size={24} className="text-[#64748B]" />
              </button>
            </div>
            
            <div className="px-3 pb-3">
              <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(searchQuery); }} className="flex items-center w-full" style={{ height: '56px', border: '2px solid #E8650A', borderRadius: '28px', background: '#F8F9FA', overflow: 'hidden' }}>
                <motion.button type="submit" initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }} className="flex items-center justify-center flex-shrink-0" style={{ width: '56px', height: '56px', background: '#E8650A', borderRadius: '26px 0 0 26px', position: 'relative', left: '-2px' }}>
                  <Search size={24} className="text-white" />
                </motion.button>
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Vidéo, artisan, métier..." className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none" style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }} autoFocus />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="flex items-center justify-center mr-4" style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}>
                    <X size={14} className="text-white" />
                  </button>
                )}
              </form>
            </div>
            
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 py-4">
              <button onClick={() => { setShowFullscreenSearch(false); setTimeout(() => setShowFullFilters(true), 150); }} className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)', boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)' }}>
                <SlidersHorizontal size={20} />
                <span style={{ fontSize: '15px' }}>Trouve ta vidéo en 3 clics</span>
              </button>
            </motion.div>
            
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">Recherche dans : Cinéma</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold">Recherches récentes</p>
                  {recentSearches.length > 0 && <button onClick={clearRecentSearches} className="text-[11px] text-[#E8650A] font-medium">Effacer</button>}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button key={idx} onClick={() => handleSearchSubmit(search)} className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg">
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Reels Player Fullscreen */}
      <AnimatePresence>
        {selectedVideoIndex !== null && (
          <ReelsPlayer 
            videos={videos} 
            initialIndex={selectedVideoIndex} 
            onClose={() => setSelectedVideoIndex(null)} 
          />
        )}
      </AnimatePresence>
      
      {/* UnifiedFilterSystem */}
      <UnifiedFilterSystem context="cinema" isOpen={showFullFilters} onClose={() => setShowFullFilters(false)} filters={activeFilters} onFiltersChange={setActiveFilters} resultsCount={videos.length} onApply={() => setShowFullFilters(false)} />
      
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
